// react core
import { useState, useEffect, useCallback } from "react";

// aws services
import {
  getCurrentUser,
  fetchAuthSession,
  fetchDevices,
  fetchUserAttributes,
  fetchMFAPreference,
  updateUserAttributes,
  sendUserAttributeVerificationCode,
  confirmUserAttribute,
  setUpTOTP,
  verifyTOTPSetup,
  updatePassword,
  updateMFAPreference,
  signOut,
} from "aws-amplify/auth";

// date handling
import dayjs from "dayjs";

// entzy models and services
import configEntzy from "components/config/ConfigEntzy";
import { createIdentityUserItem } from "models/identity";
import { serviceObjectDownload } from "services/storage/object";
import { serviceLogError } from "services/graphql/call";

const getProviderFromUsername = (username) => {
  if (username.startsWith("Amazon")) {
    return "amazon";
  }
  if (username.startsWith("Google")) {
    return "google";
  }
  if (username.startsWith("Apple")) {
    return "apple";
  }
  return "internal";
};

const getNameFromAttributes = (attributes) => {
  let name = "set your name";
  if (
    attributes["custom:entzy_name"] &&
    attributes["custom:entzy_name"].length > 0
  ) {
    name = attributes["custom:entzy_name"];
  } else if (attributes.name && attributes.name > 0) {
    name = attributes.name;
  }
  name = name.toLowerCase();
  name = name.replace(/\s/g, "_");
  name = name.replace(/[^0-9a-z_]/g, "");
  return name;
};

const getStatusFromAttributes = (attributes, session) => {
  const status = {};

  // use session tokens for last connect time
  status.last_connect =
    session.tokens?.accessToken?.payload?.auth_time ||
    session.tokens?.idToken?.payload?.auth_time ||
    null;

  // all other status properties from attributes
  status.device_synced = attributes["custom:entzy_device_synced"]
    ? attributes["custom:entzy_device_synced"]
    : configEntzy.DEVICE_SYNC_STAMP.generateStamp();

  status.last_email_verified_address = attributes["custom:entzy_last_email_add"]
    ? attributes["custom:entzy_last_email_add"]
    : null;
  status.last_email_verified_timestamp = attributes["custom:entzy_last_email"]
    ? attributes["custom:entzy_last_email"]
    : null;

  status.verified = attributes.email_verified
    ? String(attributes.email_verified).toLowerCase() === "true"
    : false;
  status.reset = false;

  status.payment_set = attributes["custom:entzy_payment_set"]
    ? String(attributes["custom:entzy_payment_set"]).toLowerCase() === "true"
    : false;
  status.payout_set = attributes["custom:entzy_payout_set"]
    ? String(attributes["custom:entzy_payout_set"]).toLowerCase() === "true"
    : false;

  return status;
};

const getNotificationsFromAttributes = (attributes) => {
  if (attributes["custom:entzy_notifications"]) {
    return attributes["custom:entzy_notifications"];
  } else {
    return JSON.stringify([
      { id: "emailMessagesDirect", muted: false },
      { id: "emailMessagesSocial", muted: false },
    ]);
  }
};

const getAdminFromAttributes = (attributes) => {
  if (
    attributes["cognito:groups"] &&
    attributes["cognito:groups"].includes("admin")
  ) {
    return true;
  }
  return false;
};

const getUserConnectionState = async () => {
  const data = {
    connected: false,
    attributes: null,
  };
  try {
    const userInfo = {
      current: await getCurrentUser(),
      session: await fetchAuthSession(),
      attributes: await fetchUserAttributes(),
      mfa: await fetchMFAPreference(),
      devices: await fetchDevices(),
    };

    data.stateid = Date.now();
    data.connected = true;
    data.mfa = userInfo.mfa.enabled ? "TOTP" : "NOMFA";
    data.provider = getProviderFromUsername(userInfo.current.username);

    data.identity = userInfo.session.identityId;
    data.sub = userInfo.attributes.sub;
    data.username = userInfo.current.username;
    data.name = getNameFromAttributes(userInfo.attributes);
    data.email = userInfo.attributes.email;

    data.notifications = getNotificationsFromAttributes(userInfo.attributes);

    // NOTE: avatar and tribe handled separately
    // pass base attributes for onward hydration
    data.avatar = {
      key: userInfo.attributes["custom:entzy_avatar"],
      images: {},
      hydrated: false,
    };
    data.tribe = {};

    data.admin = getAdminFromAttributes(userInfo.attributes);

    data.status = getStatusFromAttributes(
      userInfo.attributes,
      userInfo.session,
    );
  } catch (e) {
    data.alert = true;
    data.message = e;
  }

  return data;
};

const validateUserData = async (userData) => {
  // correct partial state email changes
  if (
    userData.status.last_email_verified_timestamp &&
    userData.status.last_email_verified_address !== userData.email
  ) {
    const tsDate = dayjs.unix(userData.status.last_email_verified_timestamp);
    const tsNow = dayjs();
    const tsDiff = tsNow.diff(tsDate, "minute");
    // if last email change was less than 5 minutes ago, revert to last verified email
    if (tsDiff > 5) {
      const attributes = {
        email: userData.status.last_email_verified_address,
        "custom:entzy_last_email": "",
      };
      await updateUserAttributes({ userAttributes: attributes });

      window.alert(
        "Your recent email change was unconfirmed.\n\nThe address had to be reset to your original:\n" +
          userData.status.last_email_verified_address +
          "\n\nManage email settings in your profile to try again. Remember to verify with the sent confirmation code.",
      );
      window.location.reload();
    }
  }
};

const returnDataByKey = (data, key) => {
  if (key) {
    if (Array.isArray(key)) {
      const output = {};
      for (let k of key) {
        output[k] = data[k];
      }
      return output;
    } else {
      return data[key];
    }
  } else {
    return data;
  }
};

export const userGet = async (key) => {
  const userConnection = await getUserConnectionState();

  if (!userConnection.connected) {
    return key
      ? returnDataByKey(createIdentityUserItem({}), key)
      : createIdentityUserItem({});
  }

  try {
    // create user from connection
    const userData = createIdentityUserItem({
      stateid: userConnection.stateid,
      connected: userConnection.connected,
      mfa: userConnection.mfa,
      provider: userConnection.provider,
      identity: userConnection.identity,
      sub: userConnection.sub,
      username: userConnection.username,
      name: userConnection.name,
      email: userConnection.email,
      notifications: userConnection.notifications,
      avatar: userConnection.avatar,
      tribe: userConnection.tribe,
      status: userConnection.status,
      admin: userConnection.admin,
    });

    // hydrate avatar images
    if (userData.avatar.key && !userData.avatar.key.includes(":none:")) {
      const images = await Promise.all([
        serviceObjectDownload(userData.avatar.key, "sm"),
        serviceObjectDownload(userData.avatar.key, "md"),
        serviceObjectDownload(userData.avatar.key, "lg"),
      ]);
      userData.avatar.hydrated = true;
      images.forEach((item) => {
        if (item.success) {
          userData.avatar.images[item.size] = item;
        } else {
          // any failures deactivate avatar
          userData.avatar.hydrated = false;
        }
      });
    } else {
      userData.avatar.hydrated = true;
    }

    // validate user data for consistency
    // handles any backstop conditions
    await validateUserData(userData);

    return key ? returnDataByKey(userData, key) : userData;
  } catch (e) {
    serviceLogError("userGet", e);
    return key
      ? returnDataByKey(createIdentityUserItem({}), key)
      : createIdentityUserItem({});
  }
};

export const userUpdate = async (attributes) => {
  const keys = Object.keys(attributes);
  const validKeys = [
    "name",
    "email",
    "custom:entzy_name",
    "custom:entzy_avatar",
    "custom:entzy_payment_set",
    "custom:entzy_payout_set",
    "custom:entzy_last_email",
    "custom:entzy_last_email_add",
    "custom:entzy_device_synced",
    "custom:entzy_notifications",
  ];
  let validUpdate = true;
  keys.map(function (key) {
    if (!validKeys.includes(key)) {
      validUpdate = false;
    }
    return validUpdate;
  });
  if (!validUpdate) {
    serviceLogError("userUpdate InvalidAttributes", { attributes });
    return { success: false, message: "Invalid attributes for update" };
  }
  try {
    await updateUserAttributes({ userAttributes: attributes });

    return {
      success: true,
      data: await userGet(),
    };
  } catch (error) {
    serviceLogError("userUpdate", error);
    return { success: false, message: error.message };
  }
};

export const userUpdateTimestamp = () => {
  return dayjs().unix();
};

export const userEmailVerify = async (email, code) => {
  try {
    const user = await getUserConnectionState();
    if (user.connected) {
      if (code) {
        await confirmUserAttribute({
          userAttributeKey: "email",
          confirmationCode: code,
        });
      } else {
        await sendUserAttributeVerificationCode({
          userAttributeKey: "email",
        });
      }
      return {
        success: true,
      };
    } else {
      return {
        success: false,
        message: "Please connect to manage email",
      };
    }
  } catch (error) {
    serviceLogError("userEmailVerify", error);
    return { success: false, message: error.message };
  }
};

export const userPasswordUpdate = async (credentials) => {
  try {
    const user = await getUserConnectionState();
    if (user.connected) {
      await updatePassword({
        oldPassword: credentials.currentPassword,
        newPassword: credentials.newPassword,
      });

      return {
        success: true,
      };
    } else {
      return {
        success: false,
        message: "Please connect to manage password",
      };
    }
  } catch (error) {
    serviceLogError("userPasswordUpdate", error);
    return { success: false, message: error.message };
  }
};

export const userMfaSetup = async () => {
  try {
    const user = await getUserConnectionState();
    if (user.connected) {
      // initiate mfa setup and get shared secret
      const setup = await setUpTOTP();
      const secret = setup.sharedSecret;
      const appName = configEntzy.APP_DOMAIN;
      const appAccount = user.email;

      // generate qr code for app scanning
      // qrCode created as: `otpauth://totp/${appName}:${appAccount}?secret=${secret}&issuer=${appName}`;
      const qrCode = setup.getSetupUri(appName, appAccount).href;

      return {
        success: true,
        secret,
        qrCode,
      };
    } else {
      return {
        success: false,
        message: "Please connect to manage MFA",
      };
    }
  } catch (error) {
    serviceLogError("userMfaSetup", error);
    return { success: false, message: error.message };
  }
};

export const userMfaActivate = async (code) => {
  try {
    const user = await getUserConnectionState();
    if (user.connected) {
      await verifyTOTPSetup({ code });
      await updateMFAPreference({ totp: "PREFERRED" });
      return {
        success: true,
      };
    } else {
      return {
        success: false,
        message: "Please connect to manage MFA",
      };
    }
  } catch (error) {
    serviceLogError("userMfaActivate", error);
    return { success: false, message: error.message };
  }
};

export const userMfaDeactivate = async (code) => {
  try {
    const user = await getUserConnectionState();
    if (user.connected) {
      await updateMFAPreference({ totp: "DISABLED" });
      return {
        success: true,
      };
    } else {
      return {
        success: false,
        message: "Please connect to manage MFA",
      };
    }
  } catch (error) {
    serviceLogError("userMfaDeactivate", error);
    return { success: false, message: error.message };
  }
};

export const userExit = async () => {
  try {
    await signOut();

    return {
      success: true,
    };
  } catch (error) {
    serviceLogError("userExit", error);
    return { success: false, message: error.message };
  }
};

export const useIdentityState = (setContextUser) => {
  const [identifying, setIdentifying] = useState(true);
  const [user, setUser] = useState(null);

  const userReload = useCallback(async (userState) => {
    setIdentifying(true);
    if (!userState) {
      userState = await userGet();
    }
    setUser(userState);
    setIdentifying(false);
    return userState;
  }, []);

  useEffect(() => {
    let cancel = false;
    const fetchUser = async () => {
      setIdentifying(true);
      const results = await userGet();
      if (!cancel) {
        setUser(results);
        if (setContextUser) {
          setContextUser(results);
        }
      }
      setIdentifying(false);
    };
    fetchUser();
    return () => {
      cancel = true;
    };
  }, [setContextUser]);

  return [identifying, user, userReload];
};

// USAGE
// // identity hook
// const [identifying, user] = useIdentityUserState();
